import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

// ============================================================================
// [영화시세 2026-08-08] 씨네체인 홈페이지 "영화시세" 입력 화면
//   사장님이 CGV/롯데시네마/메가박스/장실장님 4개 금액을 날짜별로 직접 입력.
//   - 저장된 값은 crm_unchain.ants_agent_cost(대행원가)에 그대로 들어가고,
//     영화거래소가 조회할 때 브랜드값 vs 장실장님 중 큰 값으로 치환돼 판매가에 반영됨.
//   - 입력/저장은 관리자(user_id 화이트리스트)만. 이력 표는 누구나 볼 수 있음(공개 메뉴).
//   - 관리자 여부는 서버 /request/session 의 isPriceAdmin 으로 판별(서버가 신뢰 근거).
//   - 날짜/저장 시각은 서버가 KST 헬퍼로 찍음(프론트는 표시만).
// ============================================================================

const Wrapper = styled.div`
  background-color: white;
  width: 90%;
  max-width: 900px;
  margin: 0 auto;
  padding: 24px 20px 60px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  border-bottom: 2px solid #3498db;
  padding-bottom: 8px;
  margin: 12px 0 20px;
`;

const Title = styled.h2`
  margin: 0;
  @media (max-width: 425px) { font-size: 1.5rem; }
`;

const SubTitle = styled.span`
  font-size: 0.8rem;
  color: #666;
`;

const Card = styled.div`
  border: 1px solid #e3e3e3;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 28px;
  background: #fafcff;
`;

const Field = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  flex-wrap: wrap;

  label { width: 90px; font-weight: 600; color: #333; }
  input { flex: 0 0 auto; }
`;

const PriceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin: 8px 0 16px;

  @media (max-width: 640px) { grid-template-columns: repeat(2, 1fr); }
`;

const PriceBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  span { font-size: 0.85rem; font-weight: 600; color: #444; }
  span.won { font-weight: 400; color: #888; font-size: 0.75rem; }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 1rem;
  width: 100%;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #3498db; }
`;

const DateInput = styled(Input)`
  width: 180px;
`;

const SaveButton = styled.button`
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 11px 26px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #2c80b8; }
  &:disabled { background: #b5c9d6; cursor: not-allowed; }
`;

const Message = styled.div`
  margin-top: 10px;
  font-size: 0.9rem;
  color: ${(p) => (p.$error ? '#c0392b' : '#27ae60')};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  th, td { padding: 10px 8px; text-align: right; border-bottom: 1px solid #eee; }
  th:first-child, td:first-child { text-align: left; }
  th { background: #f5f8fb; color: #333; font-weight: 600; }
  tbody tr:hover { background: #f0f7ff; cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')}; }
  td.author { text-align: center; color: #888; font-size: 0.8rem; }
`;

const won = (v) => (v === null || v === undefined || v === '') ? '-' : Number(v).toLocaleString() + '원';

// author 표시: 'manual:xxx' → '직접입력', 그 외(dexterity 등) → '자동'
const authorLabel = (a) => {
  if (!a) return '자동';
  return String(a).startsWith('manual:') ? '직접입력' : '자동';
};

export default function MoviePrice() {
  const [canEdit, setCanEdit] = useState(false); // 로그인하면 true → 입력칸 노출(로그인한 누구나 입력 가능)
  const [form, setForm] = useState({ applied_date: '', cgv_cost: '', lotte_cost: '', mega_cost: '', janga_cost: '' });
  const [list, setList] = useState([]);
  const [msg, setMsg] = useState(null); // { text, error }
  const [saving, setSaving] = useState(false);

  // KST 오늘(브라우저 로컬이 KST라는 전제 없이 안전하게) — 서버 저장은 서버가 KST로 찍으므로
  // 여기 기본값은 사용자 편의용. Asia/Seoul 기준으로 계산.
  const kstToday = () => {
    try {
      return new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' }); // YYYY-MM-DD
    } catch (e) {
      return new Date().toISOString().slice(0, 10);
    }
  };

  const fetchList = useCallback(async () => {
    try {
      const res = await fetch('/request/getMoviePriceList?limit=60', { credentials: 'include' });
      if (res.ok) setList(await res.json());
    } catch (e) {
      console.error('시세 이력 조회 실패', e);
    }
  }, []);

  useEffect(() => {
    // 로그인 여부 확인 — 로그인했으면 입력칸 노출(로그인한 누구나 입력 가능)
    (async () => {
      try {
        const res = await fetch('/request/session', { credentials: 'include' });
        setCanEdit(res.ok); // 200=로그인됨, 401=비로그인
      } catch (e) { /* 네트워크 오류 → 입력칸 숨김(조회만) */ }
    })();
    setForm((f) => ({ ...f, applied_date: kstToday() }));
    fetchList();
  }, [fetchList]);

  const onChange = (e) => {
    const { name, value } = e.target;
    // 금액칸은 숫자만(빈칸 허용). 날짜는 그대로.
    if (name !== 'applied_date' && value !== '' && !/^\d*\.?\d*$/.test(value)) return;
    setForm((f) => ({ ...f, [name]: value }));
    setMsg(null);
  };

  const onRowClick = (row) => {
    if (!canEdit) return; // 로그인 사용자만 프리필(수정)
    setForm({
      applied_date: row.applied_date,
      cgv_cost: row.cgv_cost ?? '',
      lotte_cost: row.lotte_cost ?? '',
      mega_cost: row.mega_cost ?? '',
      janga_cost: row.janga_cost ?? '',
    });
    setMsg(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onSave = async () => {
    if (!form.applied_date) { setMsg({ text: '날짜를 선택해주세요.', error: true }); return; }
    setSaving(true);
    setMsg(null);
    try {
      const res = await fetch('/request/saveMoviePrice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setMsg({ text: `${form.applied_date} 시세가 저장되었습니다.`, error: false });
        fetchList();
      } else {
        setMsg({ text: data.message || '저장에 실패했습니다.', error: true });
      }
    } catch (e) {
      setMsg({ text: '저장 요청 중 문제가 발생했습니다.', error: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Wrapper>
      <TitleRow>
        <Title>영화시세</Title>
        <SubTitle>극장별 대행 원가를 날짜별로 관리합니다.</SubTitle>
      </TitleRow>

      {canEdit && (
        <Card>
          <Field>
            <label>적용 날짜</label>
            <DateInput type="date" name="applied_date" value={form.applied_date} onChange={onChange} />
          </Field>

          <PriceGrid>
            <PriceBox>
              <span>CGV <span className="won">(원)</span></span>
              <Input type="text" inputMode="numeric" name="cgv_cost" value={form.cgv_cost} onChange={onChange} placeholder="0" />
            </PriceBox>
            <PriceBox>
              <span>롯데시네마 <span className="won">(원)</span></span>
              <Input type="text" inputMode="numeric" name="lotte_cost" value={form.lotte_cost} onChange={onChange} placeholder="0" />
            </PriceBox>
            <PriceBox>
              <span>메가박스 <span className="won">(원)</span></span>
              <Input type="text" inputMode="numeric" name="mega_cost" value={form.mega_cost} onChange={onChange} placeholder="0" />
            </PriceBox>
            <PriceBox>
              <span>장실장님 <span className="won">(원)</span></span>
              <Input type="text" inputMode="numeric" name="janga_cost" value={form.janga_cost} onChange={onChange} placeholder="0" />
            </PriceBox>
          </PriceGrid>

          <SaveButton onClick={onSave} disabled={saving}>
            {saving ? '저장 중…' : '저장'}
          </SaveButton>
          {msg && <Message $error={msg.error}>{msg.text}</Message>}
        </Card>
      )}

      <Table $clickable={canEdit}>
        <thead>
          <tr>
            <th>날짜</th>
            <th>CGV</th>
            <th>롯데시네마</th>
            <th>메가박스</th>
            <th>장실장님</th>
            <th style={{ textAlign: 'center' }}>구분</th>
          </tr>
        </thead>
        <tbody>
          {list.length === 0 ? (
            <tr><td colSpan={6} style={{ textAlign: 'center', color: '#999', padding: '30px' }}>등록된 시세가 없습니다.</td></tr>
          ) : (
            list.map((row) => (
              <tr key={row.id} onClick={() => onRowClick(row)}>
                <td>{row.applied_date}</td>
                <td>{won(row.cgv_cost)}</td>
                <td>{won(row.lotte_cost)}</td>
                <td>{won(row.mega_cost)}</td>
                <td>{won(row.janga_cost)}</td>
                <td className="author">{authorLabel(row.author)}</td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </Wrapper>
  );
}
